import { useState } from 'react';
import { ANIMALS, COLORS } from '../data/looks';
import type { Animal, Color } from '../data/looks';
import { AnimalIcon, COLOR_LABELS, LABELS } from './animals';
import { Hint, PrimaryButton, Screen, SecondaryButton, Title } from './kit';

export interface Look {
  color: Color;
  animal: Animal;
}

interface Props {
  title: string;
  initial: Look;
  /** Сочетания, занятые другими живыми профилями. Гасятся, а не прячутся:
   *  спрятанная клетка выглядит как отсутствующий зверь, и ребёнок ищет
   *  своего ежа среди четырёх, когда их пять. */
  taken: Look[];
  submitLabel: string;
  onSubmit: (look: Look) => void;
  /** Не задан — кнопки отмены нет. Так выглядит экран первого профиля:
   *  отменять там некуда, приложением без ребёнка не пользуются. */
  onCancel?: () => void;
  /** Отказ базы. Проверка занятости есть и на экране, но между отрисовкой и
   *  нажатием помещается вторая вкладка. */
  error?: string | null;
  busy?: boolean;
}

const CELL = 'flex min-h-16 items-center justify-center rounded-xl border-2 p-2';
const OFF = 'border-line bg-card';
const ON = 'border-accent bg-accent-soft';
const DEAD = 'border-line bg-card opacity-30';

/** Выбор облика: сначала зверь, потом цвет. Порядок не случаен — ребёнок
 *  выбирает «кто я», а цвет только уточняет. */
export function LookPicker({
  title, initial, taken, submitLabel, onSubmit, onCancel, error, busy,
}: Props) {
  const [animal, setAnimal] = useState<Animal>(initial.animal);
  const [color, setColor] = useState<Color>(initial.color);

  const isTaken = (c: Color, a: Animal) => taken.some((t) => t.color === c && t.animal === a);
  /** Зверь недоступен целиком, когда занят во всех шести цветах. */
  const animalDead = (a: Animal) => COLORS.every((c) => isTaken(c, a));
  const chosenTaken = isTaken(color, animal);

  return (
    <Screen>
      <Title>{title}</Title>
      <Hint>Выбери зверя и цвет. Имя не нужно — тебя узнают по картинке.</Hint>

      <div className="flex items-center justify-center py-2">
        <AnimalIcon animal={animal} color={color} size={96} />
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-[0.95rem] text-muted">Зверь</legend>
        <div className="grid grid-cols-4 gap-2">
          {ANIMALS.map((a) => {
            const dead = animalDead(a);
            return (
              <button
                key={a}
                type="button"
                disabled={dead}
                aria-pressed={a === animal}
                aria-label={LABELS[a]}
                onClick={() => setAnimal(a)}
                className={`${CELL} ${dead ? DEAD : a === animal ? ON : OFF}`}
              >
                <AnimalIcon animal={a} color={color} size={40} />
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-[0.95rem] text-muted">Цвет</legend>
        <div className="grid grid-cols-6 gap-2">
          {COLORS.map((c) => {
            const dead = isTaken(c, animal);
            return (
              <button
                key={c}
                type="button"
                disabled={dead}
                aria-pressed={c === color}
                aria-label={COLOR_LABELS[c]}
                onClick={() => setColor(c)}
                className={`${CELL} ${dead ? DEAD : c === color ? ON : OFF}`}
              >
                <AnimalIcon animal={animal} color={c} size={28} />
              </button>
            );
          })}
        </div>
      </fieldset>

      {chosenTaken && (
        <Hint>Такой {LABELS[animal]} уже есть. Выбери другой цвет или другого зверя.</Hint>
      )}
      {/* Отказ объявляется немедленно: он приходит после нажатия, когда
          взгляд уже ушёл с экрана. К одной клетке он не относится — к
          выбору целиком, — поэтому с полем не связан. */}
      {error && <p role="alert" className="m-0 text-[0.95rem] text-incorrect">{error}</p>}

      {onCancel && (
        <SecondaryButton bottom onClick={onCancel}>
          Отмена
        </SecondaryButton>
      )}
      <PrimaryButton
        bottom={!onCancel}
        disabled={chosenTaken || busy}
        onClick={() => onSubmit({ color, animal })}
      >
        {submitLabel}
      </PrimaryButton>
    </Screen>
  );
}
